import { z } from "zod/v4";
import {
  MarketShippingBackupSnapshotSchema,
  MarketShippingBackupTargetSchema,
} from "./shopify-shipping";

const backupTimestamps = {
  backedUpTs: z.number().optional(),
  restoredTs: z.number().nullish(),
};

/**
 * Each variant declares only its own fields. Columns that belong to the other
 * shipping model are unknown keys here, so a model row carrying `null` for them
 * parses without any caller-side scrubbing.
 */
export const LegacyShippingProfileDtoSchema = z.object({
  ...backupTimestamps,
  shippingModel: z.literal("legacy").optional(),
  autoRestore: z.boolean(),
  profileId: z.string(),
  profileName: z.string().nullish(),
  locationGroupId: z.string(),
  methodDefinitionId: z.string(),
  methodDefinitionConfig: z.any(),
  experienceId: z.uuid(),
  zoneId: z.string(),
  zoneName: z.string().nullish(),
});

export const MarketShippingProfileDtoSchema = z.object({
  ...backupTimestamps,
  id: z.uuid().optional(),
  shippingModel: z.literal("markets"),
  autoRestore: z.boolean(),
  experienceId: z.uuid(),
  marketTarget: MarketShippingBackupTargetSchema,
  marketSnapshot: MarketShippingBackupSnapshotSchema,
});

export const ShippingProfileDtoSchema = z.discriminatedUnion("shippingModel", [
  LegacyShippingProfileDtoSchema,
  MarketShippingProfileDtoSchema,
]);

export type LegacyShippingProfileDto = z.infer<
  typeof LegacyShippingProfileDtoSchema
>;

export type MarketShippingProfileDto = z.infer<
  typeof MarketShippingProfileDtoSchema
>;

export type ShippingProfileDto = z.infer<typeof ShippingProfileDtoSchema>;

/** Legacy is the default model, so an absent discriminant means legacy. */
export const isLegacyShippingBackup = (
  backup: ShippingProfileDto
): backup is LegacyShippingProfileDto => backup.shippingModel !== "markets";

export const isMarketShippingBackup = (
  backup: ShippingProfileDto
): backup is MarketShippingProfileDto => backup.shippingModel === "markets";
